/**
 * Audit Trail — every recorded action, newest first.
 *
 * Search and paging both happen server-side. The log grows without bound, so
 * fetching it whole to filter in the browser would work in UAT and fail in the
 * year the system is actually useful.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { fmtDateTime } from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { DataTable, type Column } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { ErrorNote, QueuePanel } from '@/components/QueueKit';
import { LoadingBlock } from '@/components/Spinner';
import '@/styles/pages.css';

interface AuditRow {
  id: number;
  ts: number;
  user: string;
  action: string;
  requestId: string;
  details: string;
}

const PAGE_SIZE = 100;

export function AuditTrailPage(): ReactNode {
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [exhausted, setExhausted] = useState(false);

  // Guards against a slow first page landing after a faster later search and
  // overwriting it.
  const generation = useRef(0);

  const fetchPage = useCallback(
    async (term: string, before?: number): Promise<void> => {
      const run = ++generation.current;
      try {
        const page = await api.admin.auditTrail<AuditRow[]>({
          limit: PAGE_SIZE,
          search: term,
          ...(before === undefined ? {} : { before }),
        });
        if (run !== generation.current) return;
        setError(null);
        setExhausted(page.length < PAGE_SIZE);
        setRows((prev) => (before === undefined ? page : [...(prev ?? []), ...page]));
      } catch (err) {
        if (run !== generation.current) return;
        setError(errorMessage(err));
      }
    },
    [],
  );

  // Debounced so typing a request id does not fire a query per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setRows(null);
      void fetchPage(search.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [search, fetchPage]);

  const loadMore = useCallback(async (): Promise<void> => {
    const last = rows?.[rows.length - 1];
    if (!last) return;
    setLoadingMore(true);
    await fetchPage(search.trim(), last.ts);
    setLoadingMore(false);
  }, [rows, search, fetchPage]);

  const columns: ReadonlyArray<Column<AuditRow>> = [
    { key: 'ts', header: 'When', cell: (r) => fmtDateTime(r.ts), width: '190px' },
    { key: 'user', header: 'Who', cell: (r) => r.user },
    { key: 'action', header: 'Action', cell: (r) => <b>{r.action}</b> },
    {
      key: 'request',
      header: 'Request',
      cell: (r) => (r.requestId ? <code>{r.requestId}</code> : '—'),
    },
    { key: 'details', header: 'Details', cell: (r) => r.details || '—' },
  ];

  return (
    <>
      <ErrorNote message={error} />

      <QueuePanel title="Audit Trail" count={rows?.length}>
        <div className="filter-row">
          <div className="field">
            <label htmlFor="audit-search">Search</label>
            <input
              id="audit-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Action, request ID, person, or details"
            />
          </div>
        </div>

        {rows === null ? (
          <LoadingBlock label="Loading the audit trail…" />
        ) : (
          <>
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(r) => String(r.id)}
              empty={
                <EmptyState
                  icon="🕒"
                  title={search ? 'No entry matches that search.' : 'Nothing has been recorded yet.'}
                />
              }
            />
            {rows.length > 0 && !exhausted ? (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => void loadMore()}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            ) : null}
            {exhausted && rows.length > 0 ? (
              <p className="hint">That is the whole trail for this filter.</p>
            ) : null}
          </>
        )}
      </QueuePanel>
    </>
  );
}
